import React, { useState, useEffect } from 'react';
import { getMembershipCard } from '../services/api';
import './MembershipCard.css';

function MembershipCard() {
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    fetchCardData();
  }, []);

  const fetchCardData = async () => {
    try {
      const response = await getMembershipCard();
      setCardData(response.data);
    } catch (error) {
      console.error('Failed to fetch membership card:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateQRCode = () => {
    if (!cardData) return null;
    
    // Simple SVG QR code representation
    // In production, use a library like qrcode.react
    const qrSvg = `
      <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
        <rect width="200" height="200" fill="white"/>
        <rect x="20" y="20" width="50" height="50" fill="black"/>
        <rect x="130" y="20" width="50" height="50" fill="black"/>
        <rect x="20" y="130" width="50" height="50" fill="black"/>
        <rect x="45" y="45" width="20" height="20" fill="white"/>
        <rect x="155" y="45" width="20" height="20" fill="white"/>
        <rect x="45" y="155" width="20" height="20" fill="white"/>
        <text x="100" y="110" textAnchor="middle" fontSize="12" fill="black">${cardData.membershipId}</text>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(qrSvg)}`;
  };

  const handleAddToAppleWallet = () => {
    // Generate a simple .pkpass structure (simplified for demo)
    alert('🍎 Membership card added to Apple Wallet!\n\nIn production, this would download a .pkpass file.');
  };

  const handleAddToGoogleWallet = () => {
    alert('🅖 Membership card added to Google Wallet!\n\nIn production, this would open Google Wallet save link.');
  };

  const handleDownloadCard = () => {
    if (!cardData) return;
    
    // Create a canvas to generate card image
    const canvas = document.createElement('canvas');
    canvas.width = 600;
    canvas.height = 350;
    const ctx = canvas.getContext('2d');

    // Card background with gradient
    const gradient = ctx.createLinearGradient(0, 0, 600, 350);
    gradient.addColorStop(0, '#667eea');
    gradient.addColorStop(1, '#764ba2');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 600, 350);

    // Card border
    ctx.strokeStyle = 'rgba(255,255,255,0.3)';
    ctx.lineWidth = 2;
    ctx.strokeRect(10, 10, 580, 330);

    // Logo/Title
    ctx.fillStyle = 'white';
    ctx.font = 'bold 28px Arial';
    ctx.fillText('🛸 Alien Loyalty', 30, 50);

    // Membership label
    ctx.font = '16px Arial';
    ctx.fillStyle = 'rgba(255,255,255,0.8)';
    ctx.fillText('MEMBERSHIP CARD', 30, 80);

    // Member name
    ctx.fillStyle = 'white';
    ctx.font = 'bold 32px Arial';
    ctx.fillText(cardData.user.name, 30, 130);

    // Member ID
    ctx.font = '24px monospace';
    ctx.fillText(`ID: ${cardData.membershipId}`, 30, 170);

    // Points
    ctx.font = '20px Arial';
    ctx.fillText(`Points: ${cardData.user.points}`, 30, 210);
    ctx.fillText(`Tier: ${cardData.user.tier.toUpperCase()}`, 30, 240);

    // QR Code placeholder
    ctx.fillStyle = 'white';
    ctx.fillRect(420, 80, 150, 150);
    ctx.fillStyle = 'black';
    ctx.fillRect(435, 95, 30, 30);
    ctx.fillRect(525, 95, 30, 30);
    ctx.fillRect(435, 185, 30, 30);
    ctx.fillStyle = 'white';
    ctx.fillRect(445, 105, 10, 10);
    ctx.fillRect(535, 105, 10, 10);
    ctx.fillRect(445, 195, 10, 10);
    ctx.fillStyle = 'black';
    ctx.font = '12px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('SCAN', 495, 170);
    ctx.textAlign = 'left';

    // Download
    const link = document.createElement('a');
    link.download = `alien-loyalty-card-${cardData.membershipId}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  if (loading) {
    return <div className="loading">Loading membership card...</div>;
  }

  if (!cardData) {
    return <div className="error">Failed to load membership card</div>;
  }

  return (
    <div className="container membership-page">
      <div className="page-header">
        <h1>🛸 Your Membership Card</h1>
        <p>Show this card at checkout to earn points</p>
      </div>

      {/* Digital Card */}
      <div className="digital-card">
        <div className="card-header">
          <span className="card-logo">🛸</span>
          <span className="card-title">Alien Loyalty</span>
        </div>
        
        <div className="card-body">
          <div className="member-info">
            <h2>{cardData.user.name}</h2>
            <p className="member-id">ID: {cardData.membershipId}</p>
            <p className="member-tier">{cardData.user.tier.toUpperCase()} MEMBER</p>
            <p className="member-points">{cardData.user.points.toLocaleString()} Points</p>
          </div>
          
          <div className="qr-section">
            {showQR ? (
              <div className="qr-code-large">
                <img src={generateQRCode()} alt="Membership QR" />
                <p>Scan at checkout</p>
              </div>
            ) : (
              <div className="qr-preview" onClick={() => setShowQR(true)}>
                <div className="qr-placeholder">
                  <span>📱</span>
                </div>
                <p>Click to show QR</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Card Actions */}
      <div className="card-actions">
        <button className="btn btn-apple" onClick={handleAddToAppleWallet}>
          🍎 Add to Apple Wallet
        </button>
        <button className="btn btn-google" onClick={handleAddToGoogleWallet}>
          🅖 Add to Google Wallet
        </button>
        <button className="btn btn-download" onClick={handleDownloadCard}>
          💾 Download Card Image
        </button>
      </div>

      {/* Instructions */}
      <div className="card-instructions">
        <h3>How to use your card:</h3>
        <ol>
          <li>Show the QR code at checkout</li>
          <li>Staff scans it or enters your Member ID</li>
          <li>Your purchase is linked to your account</li>
          <li>Points are automatically added!</li>
        </ol>
      </div>

      {/* For Staff/POS */}
      <div className="pos-section">
        <h3>🖥️ For Staff: Manual Lookup</h3>
        <p>If QR doesn't scan, enter Member ID manually:</p>
        <code className="member-code">{cardData.membershipId}</code>
      </div>
    </div>
  );
}

export default MembershipCard;
